import React, { useCallback, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../shared/components/button';
import SvgImage from '../../shared/components/svg-image';
import { getConfig } from '../../shared/features/config/selectors';
import { isMobileUa } from '../../shared/features/uatraits/selectors';
import { i18n } from '../../shared/helpers/i18n';
import { countMetrika } from '../../shared/helpers/metrika';
import { BroadcastStatus } from '../../types';
import { BroadcastInfo } from './components/broadcast-info';
import BroadcastViewerChat from './components/broadcast-viewer-chat';
import BroadcastViewerPlayer, { BroadcastViewerPlayerProps } from './components/player';
import PlayerStub from './components/player-stub';
import {
    broadcastWithChat,
    getBroadcastLiveData,
    getBroadcastViewStatus,
    selectIsUgcLiveBroadcast,
} from './features/broadcast/selectors';
import { vhLivePostMessageEvent } from './features/broadcast/slice';
import classes from './index.css';

const chatIcon = <SvgImage imageName="chat" className={classes.BroadcastViewer__ChatButtonIcon} />;

export const BroadcastViewerPage = () => {
    const status = useSelector(getBroadcastViewStatus);
    const { caption, description, streamUrlPlayer, strmPlayerUrl, ugcLiveStatus } = useSelector(getBroadcastLiveData);
    const { vhLiveHost } = useSelector(getConfig);
    const isUgcLiveBroadcast = useSelector(selectIsUgcLiveBroadcast);
    const shouldRenderChat = useSelector(broadcastWithChat);
    const isMobile = useSelector(isMobileUa);
    const [isChatOpened, setIsChatOpened] = useState(shouldRenderChat && !isMobile);

    useEffect(() => {
        countMetrika('stream_viewers_screen', 'show');
    }, []);

    const dispatch = useDispatch();

    const onChatClose = useCallback(() => {
        setIsChatOpened(false);
    }, []);

    const onChatButtonClick = useCallback(() => {
        setIsChatOpened(true);
    }, []);

    const isStreamStarted = isUgcLiveBroadcast
        ? ugcLiveStatus === 'onair'
        : [BroadcastStatus.ENDED, BroadcastStatus.ONAIR].includes(status);
    const playerUrl = isUgcLiveBroadcast ? streamUrlPlayer : strmPlayerUrl;
    const ugcLive = useMemo<BroadcastViewerPlayerProps['ugcLive']>(() => {
        if (!isUgcLiveBroadcast) {
            return;
        }

        return {
            host: vhLiveHost,
            onMessage: (data) => dispatch(vhLivePostMessageEvent(data)),
        };
    }, [isUgcLiveBroadcast, vhLiveHost, dispatch]);

    return (
        <div
            className={cn(classes.BroadcastViewer, {
                [classes.BroadcastViewer_chatOpened]: isChatOpened,
            })}
        >
            <div className={classes.BroadcastViewer__Wrapper}>
                <div className={classes.BroadcastViewer__PlayerWrapper}>
                    <BroadcastViewerPlayer hidden={!isStreamStarted} playerUrl={playerUrl} ugcLive={ugcLive} />
                    {!isStreamStarted ? <PlayerStub /> : null}
                </div>
                {shouldRenderChat ? (
                    <>
                        <BroadcastViewerChat onClose={onChatClose} visible={isChatOpened} />
                        {isMobile && !isChatOpened ? (
                            <Button
                                className={cn(classes.BroadcastViewer__ChatButton, {
                                    [classes.ChatButtonRight]: isStreamStarted,
                                })}
                                view="neutral"
                                size="md"
                                pin="round-round"
                                iconLeft={chatIcon}
                                onClick={onChatButtonClick}
                            >
                                {i18n('toolbar', 'chat-button-title')}
                            </Button>
                        ) : null}
                    </>
                ) : null}
            </div>
            {isMobile ? null : (
                <>
                    <h1 className={classes.BroadcastViewer__Title}>{caption}</h1>
                    <BroadcastInfo />
                    {description ? <div className={classes.BroadcastViewer__Description}>{description}</div> : null}
                </>
            )}
        </div>
    );
};
